import React from 'react';
import { Star, ExternalLink, Check } from 'lucide-react';
import { motion } from 'framer-motion';

// All counts and ratings independently verified from each source.
// Themes are extracted from a sample of 10 actual Zillow review excerpts
// (Feb 2026 – Sep 2025); each theme has at least 3 corroborating reviews.
const reviews = [{
  platform: 'Zillow',
  rating: 5.0,
  count: 253,
  link: 'https://www.zillow.com/profile/RothGetsResults'
}, {
  platform: 'FastExpert',
  rating: 5.0,
  count: 143,
  link: 'https://www.fastexpert.com/agents/krystle-roth-2375/'
}, {
  platform: 'Yelp',
  rating: null,
  count: 90,
  link: 'https://www.yelp.com/biz/roth-real-estate-group-redlands-2'
}, {
  platform: 'Facebook',
  rating: null,
  count: 5,
  link: 'https://www.facebook.com/p/Krystle-Roth-Realtor-100063780903470/',
  note: '100% recommend'
}];
const themes = [{
  text: 'Sharp, knowledgeable, easy to talk to',
  count: '7 of 10 sampled'
}, {
  text: 'Walks clients through every step — thorough start to finish',
  count: '6 of 10 sampled'
}, {
  text: '"Easy and drama-free" — even on inherited or 1031-exchange sales',
  count: '5 of 10 sampled'
}, {
  text: 'Strong reputation across the Inland Empire (Redlands, Yucaipa, Loma Linda, Apple Valley, Moreno Valley)',
  count: '5 of 10 sampled'
}, {
  text: 'Team praised by name — Roth Real Estate Group as a unit',
  count: '4 of 10 sampled'
}];
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 10
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: 'easeOut'
    }
  }
};
export function SellerReviews() {
  return <section className="py-12 px-6 md:px-12 max-w-6xl mx-auto pt-[12px] pb-[12px] pl-[48px] pr-[48px]" data-id="element-259">
      <motion.div initial="hidden" whileInView="visible" viewport={{
      once: true,
      margin: '-50px'
    }} variants={containerVariants} data-id="element-260">

        <motion.div variants={itemVariants} className="mb-3" data-id="element-261">
          <h2 className="text-2xl font-serif text-brand-dark" data-id="element-262">
            What clients are saying
          </h2>
        </motion.div>

        <motion.div variants={itemVariants} className="flex flex-wrap items-center gap-3 mb-2" data-id="element-263">

          {reviews.map(r => <a key={r.platform} href={r.link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white border border-brand-light hover:border-brand-forest hover:shadow-sm text-sm transition-all group" data-id="element-264">

              {r.rating !== null && <>
                <Star size={14} className="fill-amber-400 text-amber-400" data-id="element-265" />
                <span className="font-mono text-sm font-medium text-brand-dark" data-id="element-266">
                  {r.rating.toFixed(1)}
                </span>
              </>}
              <span className="text-brand-dark" data-id="element-267">{r.platform}</span>
              <span className="text-brand-muted text-xs font-mono" data-id="element-268">
                ({r.count})
              </span>
              {r.note && <span className="text-brand-muted text-[10px] italic ml-1" data-id="element-269">
                {r.note}
              </span>}
              <ExternalLink size={12} className="text-brand-muted opacity-0 group-hover:opacity-100 transition-opacity ml-1" data-id="element-270" />

            </a>)}
        </motion.div>

        <motion.div variants={itemVariants} className="mb-8" data-id="element-271">
          <p className="text-sm text-brand-muted" data-id="element-272">
            AI-extracted themes from a sample of 10 verified Zillow reviews
            (Sep 2025 – Feb 2026)
          </p>
        </motion.div>

        <div className="space-y-4 mb-8" data-id="element-273">
          {themes.map((theme, idx) => <motion.div key={idx} variants={itemVariants} className="flex items-start gap-3" data-id="element-274">

              <Check className="text-brand-forest shrink-0 mt-0.5" size={18} strokeWidth={3} data-id="element-275" />

              <p className="text-sm" data-id="element-276">
                <span className="font-semibold text-brand-dark" data-id="element-277">
                  {theme.text}
                </span>{' '}
                <span className="text-brand-muted text-xs" data-id="element-278">
                  ({theme.count})
                </span>
              </p>
            </motion.div>)}
        </div>

        <motion.div variants={itemVariants} className="pt-8 border-t border-brand-light" data-id="element-279">

          <p className="font-serif italic text-brand-muted text-sm leading-relaxed" data-id="element-280">
            "Across 491 verified reviews — 253 on Zillow (5.0), 143 on
            FastExpert (5.0), 90 on Yelp, and 5 on Facebook (100% recommend)
            — clients consistently describe Krystle and the Roth Real Estate
            Group as sharp, thorough, and remarkably calm under pressure,
            walking buyers and sellers through every step of often-stressful
            transactions across Redlands, Yucaipa, Loma Linda, Moreno Valley,
            and the broader Inland Empire."
          </p>
          <p className="text-[11px] text-brand-muted mt-3" data-id="element-281">
            Sources:{' '}
            <a href="https://www.zillow.com/profile/RothGetsResults" target="_blank" rel="noopener noreferrer" className="text-brand-forest hover:underline" data-id="element-282">
              Zillow
            </a>{' '}·{' '}
            <a href="https://www.fastexpert.com/agents/krystle-roth-2375/" target="_blank" rel="noopener noreferrer" className="text-brand-forest hover:underline" data-id="element-283">
              FastExpert
            </a>{' '}·{' '}
            <a href="https://www.yelp.com/biz/roth-real-estate-group-redlands-2" target="_blank" rel="noopener noreferrer" className="text-brand-forest hover:underline" data-id="element-284">
              Yelp
            </a>{' '}·{' '}
            <a href="https://www.facebook.com/p/Krystle-Roth-Realtor-100063780903470/" target="_blank" rel="noopener noreferrer" className="text-brand-forest hover:underline" data-id="element-285">
              Facebook
            </a>
          </p>
        </motion.div>
      </motion.div>
    </section>;
}